import React, { useState } from 'react';
import { Video, FolderOpen, Layout, Type, SlidersHorizontal, Palette, Camera, Mic } from 'lucide-react';
import '../styles/LeftSidebar.css';
import { baseUrl } from '../config';



function LeftSidebar({ onMediaUpload, onChatOpen, onOptionSelect }) {
  // State management
  const [activeOption, setActiveOption] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTextStyle, setSelectedTextStyle] = useState(null);
  const [selectedTransition, setSelectedTransition] = useState(null);

  // File handling
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const newMediaItem = {
        id: Date.now(),
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file)
      };
      setMediaItems([...mediaItems, newMediaItem]);
      onMediaUpload(file);
    }
  };

  // Recording functionality
  const handleRecordToggle = () => {
    setIsRecording(!isRecording);
    // Here you would integrate with browser's MediaRecorder API
  };

  // Data for different options
  const templates = [
    { id: 1, name: 'Social Media Post', type: 'social' },
    { id: 2, name: 'Blog Header', type: 'blog' },
    { id: 3, name: 'Video Intro', type: 'video' }
  ];

  const textStyles = [
    { id: 1, name: 'Heading', style: 'text-style-heading' },
    { id: 2, name: 'Subheading', style: 'text-style-subheading' },
    { id: 3, name: 'Body', style: 'text-style-body' }
  ];

  const transitions = [
    { id: 1, name: 'Fade', duration: '0.3s' },
    { id: 2, name: 'Slide', duration: '0.5s' },
    { id: 3, name: 'Zoom', duration: '0.4s' }
  ];

  const brandKit = {
    colors: ['#FF5733', '#33FF57', '#3357FF'],
    fonts: ['Arial', 'Helvetica', 'Roboto'],
    logos: ['logo-dark.png', 'logo-light.png']
  };

  // Handle option selection
  const handleOptionClick = (option) => {
    setActiveOption(option);
    onOptionSelect(option);
  };

  // Render content for each option
  const renderOptionContent = () => {
    switch (activeOption) {
      case 'Record & create':
        return (
          <div className="option-content record-create">
            <button 
              onClick={handleRecordToggle}
              className={`record-button ${isRecording ? 'recording' : ''}`}
            >
              {isRecording ? <Camera size={16} /> : <Mic size={16} />}
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </button>
          </div>
        );

      case 'Content library':
        return (
          <div className="option-content content-library">
            {mediaItems.map(item => (
              <div key={item.id} className="media-item">
                <FolderOpen size={16} />
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        );

      case 'Templates':
        return (
          <div className="option-content templates">
            {templates.map(template => (
              <div 
                key={template.id} 
                className={`template-item ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                onClick={() => setSelectedTemplate(template)}
              >
                <Layout size={16} />
                {template.name}
              </div>
            ))}
          </div>
        );

      case 'Text':
        return (
          <div className="option-content text-styles">
            {textStyles.map(style => (
              <div 
                key={style.id} 
                className={`text-style-item ${selectedTextStyle?.id === style.id ? 'selected' : ''}`}
                onClick={() => setSelectedTextStyle(style)}
              >
                <Type size={16} />
                {style.name}
              </div>
            ))}
          </div>
        );

      case 'Transitions':
        return (
          <div className="option-content transitions">
            {transitions.map(transition => (
              <div 
                key={transition.id} 
                className={`transition-item ${selectedTransition?.id === transition.id ? 'selected' : ''}`}
                onClick={() => setSelectedTransition(transition)}
              >
                <SlidersHorizontal size={16} />
                {transition.name} ({transition.duration})
              </div>
            ))}
          </div>
        );

      case 'Brand kit':
        return (
          <div className="option-content brand-kit">
            <div className="brand-colors">
              <h4>Colors</h4>
              <div className="color-list">
                {brandKit.colors.map((color, index) => (
                  <div
                    key={index}
                    className="color-item"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div className="brand-fonts">
              <h4>Fonts</h4>
              <div className="font-list">
                {brandKit.fonts.map((font, index) => (
                  <div key={index} className="font-item" style={{ fontFamily: font }}>
                    {font}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const sidebarOptions = [
    { name: 'Record & create', icon: Video },
    { name: 'Content library', icon: FolderOpen },
    { name: 'Templates', icon: Layout },
    { name: 'Text', icon: Type },
    { name: 'Transitions', icon: SlidersHorizontal }, // Changed from Transition to SlidersHorizontal
    { name: 'Brand kit', icon: Palette }
  ];

  return (
    <div className="sidebar-container">
      <div className="sidebar-left">
        <div className="media-library">
          <h3>Your Media</h3>
          <input
            type="file"
            id="media-upload"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <label htmlFor="media-upload" className="import-button">
            Import Media
          </label>
        </div>

        <div className="sidebar-options">
          {sidebarOptions.map(({ name, icon: Icon }) => (
            <div
              key={name}
              className={`sidebar-item ${activeOption === name ? 'active' : ''}`}
              onClick={() => handleOptionClick(name)}
            >
              <Icon size={16} />
              <span>{name}</span>
            </div>
          ))}
          <button className="sidebar-item chat-button" onClick={onChatOpen}>
            Chat with AI
          </button>
        </div>
      </div>
      
      <div className="sidebar-content">
        {renderOptionContent()}
      </div>
    </div>
  );
}

export default LeftSidebar;
